import React from "react"
import { Table, Tag, Dropdown, Spin } from "antd"
import {
  MoreOutlined,
  EditOutlined,
  CopyOutlined,
  StopOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons"
import { translations } from "../../utils/Translation/staticTranslation"
import PlansPricingFilters from "./PlansPricingFilters"
import PlansPricingPagination from "./PlansPricingPagination"
import PlansPricingPlanCard from "./PlansPricingPlanCard"
import "./PlansPricing.css"

const PlansPricingTable = ({
  data,
  loading = false,
  isReadOnly = false,
  canDelete = true,
  searchValue,
  statusFilter,
  typeFilter,
  categoryFilter,
  viewMode,
  onSearchChange,
  onStatusFilterChange,
  onTypeFilterChange,
  onCategoryFilterChange,
  onViewModeChange,
  pagination,
  onPaginationChange,
  onEdit,
  onDuplicate,
  onDisable,
  onEnable,
  onDelete,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.plansPricingPage
  const tFeatures = t?.createPlan
  const tGrid = t?.grid || {}

  const tCard = {
    ...tGrid,
    table: t?.table,
    billing_monthly: tFeatures?.billing_monthly || "/month",
    billing_yearly: tFeatures?.billing_yearly || "/year",
    images_included_template:
      tGrid?.images_included_template || "{count} Images Included",
  }

  const getStatusTag = (status) => {
    const statusConfig = {
      Active: { color: "#027A48", bg: "#ECFDF3", border: "#ABEFC6" },
      Draft: { color: "#344054", bg: "#F2F4F7", border: "#E4E7EC" },
      Inactive: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
    }
    const config = statusConfig[status] || statusConfig.Draft
    const labelKey = status?.toLowerCase()
    const label = t?.filters?.status?.[labelKey] || status

    return (
      <Tag
        className="plans-pricing-status-tag"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          border: `1px solid ${config.border}`,
        }}
      >
        {label}
      </Tag>
    )
  }

  const getTypeLabel = (type) => {
    if (type === "One-Time") return t?.filters?.types?.one_time || "One-Time"
    if (type === "Subscription")
      return t?.filters?.types?.subscription || "Subscription"
    return type
  }

  const getCategoryLabel = (category) => {
    if (category === "Standard")
      return t?.filters?.categories?.standard || "Standard"
    if (category === "Organization")
      return t?.filters?.categories?.organization || "Organization"
    return category
  }

  const getCategoryTagClass = (category) =>
    category === "Organization"
      ? "plans-pricing-category-tag plans-pricing-category-tag--organization"
      : "plans-pricing-category-tag"

  const buildActionMenu = (record) => {
    if (isReadOnly) return null
    const items = [
      {
        key: "edit",
        label: (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EditOutlined />
            {t?.actions?.edit || "Edit Plan"}
          </span>
        ),
        onClick: () => onEdit?.(record),
      },
      {
        key: "duplicate",
        label: (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CopyOutlined />
            {t?.actions?.duplicate || "Duplicate"}
          </span>
        ),
        onClick: () => onDuplicate?.(record),
      },
      ...(record.status === "Active"
        ? [
            {
              key: "disable",
              label: (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StopOutlined />
                  {t?.actions?.disable || "Disable"}
                </span>
              ),
              onClick: () => onDisable?.(record),
            },
          ]
        : record.status === "Inactive"
          ? [
              {
                key: "enable",
                label: (
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CheckCircleOutlined />
                    {t?.actions?.enable || "Enable"}
                  </span>
                ),
                onClick: () => onEnable?.(record),
              },
            ]
          : []),
      ...(canDelete
        ? [
            {
              key: "delete",
              danger: true,
              label: (
                <span
                  className="plans-pricing-action-menu-danger"
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <DeleteOutlined />
                  {t?.actions?.delete || "Delete"}
                </span>
              ),
              onClick: () => onDelete?.(record),
            },
          ]
        : []),
    ]
    return { items }
  }

  const columns = [
    {
      title: t?.table?.plan || "Plan",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <>
          <div className="plans-pricing-plan-name">{text}</div>
          <div className="plans-pricing-plan-desc">{record.description}</div>
        </>
      ),
    },
    {
      title: t?.table?.type || "Type",
      dataIndex: "type",
      key: "type",
      width: 140,
      render: (type) => (
        <Tag className="plans-pricing-type-tag">{getTypeLabel(type)}</Tag>
      ),
    },
    {
      title: t?.table?.category || "Category",
      dataIndex: "category",
      key: "category",
      width: 140,
      render: (category) => (
        <Tag className={getCategoryTagClass(category)}>
          {getCategoryLabel(category)}
        </Tag>
      ),
    },
    {
      title: t?.table?.price || "Price",
      dataIndex: "priceLabel",
      key: "price",
      width: 140,
      render: (priceLabel, record) => (
        <>
          <div className="plans-pricing-price-main">{priceLabel}</div>
          {record.discountPercent != null && (
            <div className="plans-pricing-discount-label">
              {record.discountPercent}% {t?.off_label || "OFF"}
            </div>
          )}
        </>
      ),
    },
    {
      title: t?.table?.images || "Images",
      dataIndex: "images",
      key: "images",
      width: 90,
    },
    {
      title: t?.table?.subscribers || "Subscribers",
      dataIndex: "subscribers",
      key: "subscribers",
      width: 110,
    },
    {
      title: t?.table?.status || "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => getStatusTag(status),
    },
    {
      title: t?.table?.last_updated || "Last Updated",
      dataIndex: "lastUpdated",
      key: "lastUpdated",
      width: 130,
    },
    ...(isReadOnly
      ? []
      : [
          {
            title: t?.table?.actions || "Actions",
            key: "actions",
            width: 72,
            align: "center",
            render: (_, record) => (
              <Dropdown
                menu={buildActionMenu(record)}
                trigger={["click"]}
                overlayClassName="plans-pricing-action-dropdown"
              >
                <MoreOutlined className="plans-pricing-actions-icon" />
              </Dropdown>
            ),
          },
        ]),
  ]

  const renderPagination = (extraClass = "") => {
    if (!pagination || !onPaginationChange) return null
    return (
      <PlansPricingPagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.total}
        onChange={onPaginationChange}
        className={extraClass}
      />
    )
  }

  const renderGrid = () => {
    if (!data.length) {
      return (
        <div className="plans-pricing-empty">
          {t?.no_plans || "No plans match your filters."}
        </div>
      )
    }

    return (
      <>
        <div className="plans-pricing-grid">
          {data.map((plan) => (
            <PlansPricingPlanCard
              key={plan.id}
              plan={plan}
              t={tCard}
              tFeatures={tFeatures}
              actionMenu={buildActionMenu(plan)}
              getStatusTag={getStatusTag}
              getTypeLabel={getTypeLabel}
              getCategoryLabel={getCategoryLabel}
            />
          ))}
        </div>
        {renderPagination("plans-pricing-pagination--grid")}
      </>
    )
  }

  return (
    <div className="plans-pricing-table-wrapper">
      <PlansPricingFilters
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        typeFilter={typeFilter}
        onTypeFilterChange={onTypeFilterChange}
        categoryFilter={categoryFilter}
        onCategoryFilterChange={onCategoryFilterChange}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
      />

      {viewMode === "list" ? (
        <div className="plans-pricing-table-container">
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            rowKey="id"
            pagination={false}
            locale={{
              emptyText: t?.no_plans || "No plans match your filters.",
            }}
          />
          {renderPagination()}
        </div>
      ) : loading ? (
        <div className="plans-pricing-empty">
          <Spin />
        </div>
      ) : (
        renderGrid()
      )}
    </div>
  )
}

export default PlansPricingTable
